/**
 * CCTVDashboard - CCTV / Camera Management Module
 * Sub-components extracted to /components/cctv/
 */
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';

import {
  Camera, Video, Plus, Edit2, Eye, Settings, Search, RefreshCw,
  Wifi, WifiOff, AlertTriangle, CheckCircle, Clock, Download, Play,
  Grid3X3, List, ArrowLeft, Bell, Image, FolderOpen, Loader2,
  FileVideo, ImageIcon as ImageLucide, Brain
} from 'lucide-react';

import { API_URL, DEVICE_TYPES, ALERT_TYPES } from './cctv/CCTVConstants';
import { CCTVDeviceForm } from './cctv/CCTVDeviceForm';
import { CCTVDeviceCard } from './cctv/CCTVDeviceCard';
import { CCTVLiveView } from './cctv/CCTVLiveView';
import { CCTVLiveWall } from './cctv/CCTVLiveWall';
import { CCTVSettings } from './cctv/CCTVSettings';
import { CCTVRulesEditor } from './cctv/CCTVRulesEditor';
import { getToken } from '@/lib/authToken';
import { useOpsRole } from '@/hooks/useOpsRole';

const CCTVDashboard = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [showAddDevice, setShowAddDevice] = useState(false);
  const [editingDevice, setEditingDevice] = useState(null);
  const [selectedDevice, setSelectedDevice] = useState(null);
  const [deviceFilters, setDeviceFilters] = useState({ status: 'all', type: 'all', location: 'all' });
  const [searchQuery, setSearchQuery] = useState('');
  const [viewMode, setViewMode] = useState('grid');
  // Role-gating — only admins/supervisors with cctv.cameras can add/edit
  // devices; operators see live + recordings + album only.
  const { can, isSuper, loading: roleLoading } = useOpsRole('cctv');

  const token = getToken();
  const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };

  // ============== QUERIES ==============
  const { data: stats = {}, isLoading: statsLoading } = useQuery({
    queryKey: ['cctv-stats'],
    queryFn: async () => { const res = await fetch(`${API_URL}/api/cctv/stats`, { headers }); if (!res.ok) return {}; return res.json(); },
    refetchInterval: 30000
  });

  const { data: devicesData = { devices: [] }, isLoading: devicesLoading, refetch: refetchDevices } = useQuery({
    queryKey: ['cctv-devices', deviceFilters, searchQuery],
    queryFn: async () => {
      let url = `${API_URL}/api/cctv/devices?limit=100`;
      if (deviceFilters.status !== 'all') url += `&status=${deviceFilters.status}`;
      if (deviceFilters.type !== 'all') url += `&device_type=${deviceFilters.type}`;
      if (deviceFilters.location !== 'all') url += `&location=${deviceFilters.location}`;
      if (searchQuery) url += `&search=${encodeURIComponent(searchQuery)}`;
      const res = await fetch(url, { headers }); if (!res.ok) return { devices: [] }; return res.json();
    }
  });

  const { data: locationsData = { locations: [] } } = useQuery({
    queryKey: ['cctv-locations'],
    queryFn: async () => { const res = await fetch(`${API_URL}/api/cctv/locations`, { headers }); if (!res.ok) return { locations: [] }; return res.json(); }
  });

  const { data: recordingsData = { recordings: [] } } = useQuery({
    queryKey: ['cctv-recordings'],
    queryFn: async () => { const res = await fetch(`${API_URL}/api/cctv/recordings?limit=50`, { headers }); if (!res.ok) return { recordings: [] }; return res.json(); },
    enabled: activeTab === 'recordings'
  });

  const { data: snapshotsData = { snapshots: [] } } = useQuery({
    queryKey: ['cctv-snapshots'],
    queryFn: async () => { const res = await fetch(`${API_URL}/api/cctv/snapshots?limit=50`, { headers }); if (!res.ok) return { snapshots: [] }; return res.json(); },
    enabled: activeTab === 'album'
  });

  const { data: alertsData = { alerts: [] } } = useQuery({
    queryKey: ['cctv-alerts'],
    queryFn: async () => { const res = await fetch(`${API_URL}/api/cctv/alerts?limit=50`, { headers }); if (!res.ok) return { alerts: [] }; return res.json(); },
    enabled: activeTab === 'alerts'
  });

  const { data: settings = {} } = useQuery({
    queryKey: ['cctv-settings'],
    queryFn: async () => { const res = await fetch(`${API_URL}/api/cctv/settings`, { headers }); if (!res.ok) return {}; return res.json(); },
    enabled: activeTab === 'settings'
  });

  // ============== MUTATIONS ==============
  const addDeviceMutation = useMutation({
    mutationFn: async (data) => { const res = await fetch(`${API_URL}/api/cctv/devices`, { method: 'POST', headers, body: JSON.stringify(data) }); if (!res.ok) throw new Error((await res.json()).detail || 'Failed'); return res.json(); },
    onSuccess: () => { queryClient.invalidateQueries(['cctv-devices']); queryClient.invalidateQueries(['cctv-stats']); setShowAddDevice(false); toast.success('Device added'); },
    onError: (e) => toast.error(e.message)
  });

  const updateDeviceMutation = useMutation({
    mutationFn: async ({ id, data }) => { const res = await fetch(`${API_URL}/api/cctv/devices/${id}`, { method: 'PUT', headers, body: JSON.stringify(data) }); if (!res.ok) throw new Error('Failed'); return res.json(); },
    onSuccess: () => { queryClient.invalidateQueries(['cctv-devices']); setEditingDevice(null); toast.success('Device updated'); },
    onError: (e) => toast.error(e.message)
  });

  const deleteDeviceMutation = useMutation({
    mutationFn: async (id) => { const res = await fetch(`${API_URL}/api/cctv/devices/${id}`, { method: 'DELETE', headers }); if (!res.ok) throw new Error('Failed'); return res.json(); },
    onSuccess: () => { queryClient.invalidateQueries(['cctv-devices']); queryClient.invalidateQueries(['cctv-stats']); toast.success('Device deleted'); },
    onError: (e) => toast.error(e.message)
  });

  const acknowledgeAlertMutation = useMutation({
    mutationFn: async (id) => { const res = await fetch(`${API_URL}/api/cctv/alerts/${id}/acknowledge`, { method: 'PUT', headers }); if (!res.ok) throw new Error('Failed'); return res.json(); },
    onSuccess: () => { queryClient.invalidateQueries(['cctv-alerts']); toast.success('Alert acknowledged'); }
  });

  const resolveAlertMutation = useMutation({
    mutationFn: async (id) => { const res = await fetch(`${API_URL}/api/cctv/alerts/${id}/resolve`, { method: 'PUT', headers }); if (!res.ok) throw new Error('Failed'); return res.json(); },
    onSuccess: () => { queryClient.invalidateQueries(['cctv-alerts']); queryClient.invalidateQueries(['cctv-stats']); toast.success('Alert resolved'); }
  });

  const updateSettingsMutation = useMutation({
    mutationFn: async (data) => { const res = await fetch(`${API_URL}/api/cctv/settings`, { method: 'PUT', headers, body: JSON.stringify(data) }); if (!res.ok) throw new Error('Failed'); return res.json(); },
    onSuccess: () => { queryClient.invalidateQueries(['cctv-settings']); toast.success('Settings updated'); }
  });

  // ============== RENDER ==============
  return (
    <div className="min-h-screen bg-gray-50" data-testid="cctv-dashboard">
      {/* Header */}
      <div className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="sm" onClick={() => navigate('/admin')}><ArrowLeft className="h-4 w-4 mr-2" />Back</Button>
              <div>
                <h1 className="text-xl font-bold flex items-center gap-2"><Camera className="h-6 w-6" />CCTV Management</h1>
                <p className="text-sm text-muted-foreground">Camera & Recording System</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Badge variant="outline" className="gap-1">
                <div className={`w-2 h-2 rounded-full ${stats.online_devices > 0 ? 'bg-green-500' : 'bg-gray-400'}`} />
                {stats.online_devices || 0} Online
              </Badge>
              <Button onClick={() => setShowAddDevice(true)} data-testid="add-device-btn"><Plus className="h-4 w-4 mr-2" />Add Device</Button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto p-4">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-6 grid grid-cols-3 sm:grid-cols-4 h-auto gap-1 md:inline-flex md:grid-cols-none" data-testid="cctv-tabs">
            <TabsTrigger value="dashboard" className="gap-2" data-testid="cctv-tab-dashboard"><Grid3X3 className="h-4 w-4" />Dashboard</TabsTrigger>
            <TabsTrigger value="livewall" className="gap-2" data-testid="cctv-tab-livewall"><Video className="h-4 w-4" />Live Wall</TabsTrigger>
            {(isSuper || can('cctv.cameras') || roleLoading) && (
              <TabsTrigger value="devices" className="gap-2" data-testid="cctv-tab-devices"><Camera className="h-4 w-4" />Devices</TabsTrigger>
            )}
            <TabsTrigger value="recordings" className="gap-2" data-testid="cctv-tab-recordings"><FileVideo className="h-4 w-4" />Recordings</TabsTrigger>
            <TabsTrigger value="album" className="gap-2" data-testid="cctv-tab-album"><Image className="h-4 w-4" />Album</TabsTrigger>
            <TabsTrigger value="alerts" className="gap-2 relative" data-testid="cctv-tab-alerts">
              <Bell className="h-4 w-4" />Alerts
              {stats.active_alerts > 0 && <Badge variant="destructive" className="ml-1 h-5 w-5 p-0 justify-center">{stats.active_alerts}</Badge>}
            </TabsTrigger>
            {(isSuper || can('cctv.manage') || roleLoading) && (
              <TabsTrigger value="ai-rules" className="gap-2" data-testid="cctv-tab-ai-rules"><Brain className="h-4 w-4" />AI Rules</TabsTrigger>
            )}
            {(isSuper || can('cctv.manage') || roleLoading) && (
              <TabsTrigger value="settings" className="gap-2" data-testid="cctv-tab-settings"><Settings className="h-4 w-4" />Settings</TabsTrigger>
            )}
          </TabsList>

          {/* Dashboard Tab */}
          <TabsContent value="dashboard">
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
              {[
                { label: 'Total Devices', value: stats.total_devices || 0, icon: Camera, bg: 'bg-blue-100', color: 'text-blue-600' },
                { label: 'Online', value: stats.online_devices || 0, icon: Wifi, bg: 'bg-green-100', color: 'text-green-600' },
                { label: 'Offline', value: stats.offline_devices || 0, icon: WifiOff, bg: 'bg-red-100', color: 'text-red-600' },
                { label: 'Recording', value: stats.recording_devices || 0, icon: Video, bg: 'bg-red-100', color: 'text-red-600' },
                { label: 'Active Alerts', value: stats.active_alerts || 0, icon: AlertTriangle, bg: 'bg-amber-100', color: 'text-amber-600' },
              ].map(({ label, value, icon: Icon, bg, color }) => (
                <Card key={label}><CardContent className="pt-6">
                  <div className="flex items-center gap-3">
                    <div className={`p-3 ${bg} rounded-lg`}><Icon className={`h-6 w-6 ${color}`} /></div>
                    <div><p className="text-sm text-muted-foreground">{label}</p><p className="text-2xl font-bold">{value}</p></div>
                  </div>
                </CardContent></Card>
              ))}
            </div>

            <Card className="mb-6">
              <CardHeader><CardTitle>Quick Actions</CardTitle></CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                  {[
                    { label: 'Add Device', icon: Plus, action: () => setShowAddDevice(true) },
                    { label: 'Device List', icon: List, action: () => setActiveTab('devices') },
                    { label: 'Recordings', icon: FolderOpen, action: () => setActiveTab('recordings') },
                    { label: 'Album', icon: Image, action: () => setActiveTab('album') },
                    { label: 'Settings', icon: Settings, action: () => setActiveTab('settings') },
                  ].map(({ label, icon: Icon, action }) => (
                    <Button key={label} variant="outline" className="h-20 flex-col gap-2" onClick={action}><Icon className="h-6 w-6" /><span>{label}</span></Button>
                  ))}
                </div>
              </CardContent>
            </Card>

            {stats.recent_alerts?.length > 0 && (
              <Card className="mb-6">
                <CardHeader className="flex flex-row items-center justify-between"><CardTitle>Recent Alerts</CardTitle><Button variant="link" onClick={() => setActiveTab('alerts')}>View All</Button></CardHeader>
                <CardContent><div className="space-y-3">
                  {stats.recent_alerts.map((alert) => {
                    const alertType = ALERT_TYPES[alert.alert_type] || ALERT_TYPES.offline;
                    const AlertIcon = alertType.icon;
                    return (
                      <div key={alert.id} className="flex items-center gap-3 p-3 bg-muted rounded-lg">
                        <AlertIcon className={`h-5 w-5 ${alertType.color}`} />
                        <div className="flex-1"><p className="font-medium">{alert.message}</p><p className="text-sm text-muted-foreground">{alert.device_name} &bull; {new Date(alert.created_at).toLocaleString()}</p></div>
                        <Button size="sm" variant="outline" onClick={() => acknowledgeAlertMutation.mutate(alert.id)}>Acknowledge</Button>
                      </div>
                    );
                  })}
                </div></CardContent>
              </Card>
            )}

            <Card>
              <CardHeader className="flex flex-row items-center justify-between"><CardTitle>Live Cameras</CardTitle><Button variant="link" onClick={() => setActiveTab('devices')}>View All</Button></CardHeader>
              <CardContent>
                {devicesLoading ? (
                  <div className="flex items-center justify-center py-12"><Loader2 className="h-8 w-8 animate-spin text-muted-foreground" /></div>
                ) : devicesData.devices.length === 0 ? (
                  <div className="text-center py-12">
                    <Camera className="h-16 w-16 mx-auto text-muted-foreground mb-4" /><h3 className="text-lg font-medium">No devices yet</h3><p className="text-muted-foreground mb-4">Add your first camera to get started</p>
                    <Button onClick={() => setShowAddDevice(true)}><Plus className="h-4 w-4 mr-2" />Add Device</Button>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    {devicesData.devices.slice(0, 8).map((device) => (
                      <CCTVDeviceCard key={device.id} device={device} onView={setSelectedDevice} onEdit={setEditingDevice} onDelete={(id) => deleteDeviceMutation.mutate(id)} />
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Live Wall Tab */}
          <TabsContent value="livewall">
            <CCTVLiveWall
              devices={devicesData?.devices || []}
              onMaximize={setSelectedDevice}
              onRefresh={() => queryClient.invalidateQueries({ queryKey: ['cctv-devices'] })}
            />
          </TabsContent>

          {/* Devices Tab */}
          <TabsContent value="devices">
            <Card className="mb-4"><CardContent className="pt-4">
              <div className="flex flex-wrap items-center gap-4">
                <div className="relative flex-1 min-w-0 sm:min-w-[200px]"><Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" /><Input placeholder="Search devices..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="pl-9" /></div>
                <Select value={deviceFilters.status} onValueChange={(v) => setDeviceFilters({...deviceFilters, status: v})}><SelectTrigger className="w-32"><SelectValue placeholder="Status" /></SelectTrigger><SelectContent><SelectItem value="all">All Status</SelectItem><SelectItem value="online">Online</SelectItem><SelectItem value="offline">Offline</SelectItem></SelectContent></Select>
                <Select value={deviceFilters.type} onValueChange={(v) => setDeviceFilters({...deviceFilters, type: v})}><SelectTrigger className="w-32"><SelectValue placeholder="Type" /></SelectTrigger><SelectContent><SelectItem value="all">All Types</SelectItem>{DEVICE_TYPES.map(t => <SelectItem key={t.id} value={t.id}>{t.label}</SelectItem>)}</SelectContent></Select>
                <Select value={deviceFilters.location} onValueChange={(v) => setDeviceFilters({...deviceFilters, location: v})}><SelectTrigger className="w-40"><SelectValue placeholder="Location" /></SelectTrigger><SelectContent><SelectItem value="all">All Locations</SelectItem>{locationsData.locations.map(loc => <SelectItem key={loc} value={loc}>{loc}</SelectItem>)}</SelectContent></Select>
                <div className="flex items-center gap-1 border rounded-md">
                  <Button variant={viewMode === 'grid' ? 'secondary' : 'ghost'} size="sm" onClick={() => setViewMode('grid')}><Grid3X3 className="h-4 w-4" /></Button>
                  <Button variant={viewMode === 'list' ? 'secondary' : 'ghost'} size="sm" onClick={() => setViewMode('list')}><List className="h-4 w-4" /></Button>
                </div>
                <Button variant="outline" size="sm" onClick={() => refetchDevices()}><RefreshCw className="h-4 w-4" /></Button>
              </div>
            </CardContent></Card>

            {viewMode === 'grid' ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                {devicesData.devices.map((device) => (
                  <CCTVDeviceCard key={device.id} device={device} onView={setSelectedDevice} onEdit={setEditingDevice} onDelete={(id) => deleteDeviceMutation.mutate(id)} />
                ))}
              </div>
            ) : (
              <Card><Table>
                <TableHeader><TableRow><TableHead>Device</TableHead><TableHead>Serial Number</TableHead><TableHead>Type</TableHead><TableHead>Location</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Actions</TableHead></TableRow></TableHeader>
                <TableBody>
                  {devicesData.devices.map((device) => (
                    <TableRow key={device.id}>
                      <TableCell className="font-medium">{device.name}</TableCell>
                      <TableCell className="text-muted-foreground">{device.serial_number}</TableCell>
                      <TableCell>{device.device_type}</TableCell>
                      <TableCell>{device.location || '-'}</TableCell>
                      <TableCell><Badge variant={device.status?.is_online ? "default" : "destructive"}>{device.status?.is_online ? 'Online' : 'Offline'}</Badge></TableCell>
                      <TableCell className="text-right">
                        <Button variant="ghost" size="sm" onClick={() => setSelectedDevice(device)}><Eye className="h-4 w-4" /></Button>
                        <Button variant="ghost" size="sm" onClick={() => setEditingDevice(device)}><Edit2 className="h-4 w-4" /></Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table></Card>
            )}
          </TabsContent>

          {/* Recordings Tab */}
          <TabsContent value="recordings">
            <Card>
              <CardHeader><CardTitle>Local Recordings</CardTitle><CardDescription>Saved video recordings from your cameras</CardDescription></CardHeader>
              <CardContent>
                {recordingsData.recordings.length === 0 ? (
                  <div className="text-center py-12"><FileVideo className="h-16 w-16 mx-auto text-muted-foreground mb-4" /><h3 className="text-lg font-medium">No recordings yet</h3><p className="text-muted-foreground">Recordings will appear here when captured</p></div>
                ) : (
                  <Table>
                    <TableHeader><TableRow><TableHead>Device</TableHead><TableHead>Date/Time</TableHead><TableHead>Duration</TableHead><TableHead>Size</TableHead><TableHead>Trigger</TableHead><TableHead className="text-right">Actions</TableHead></TableRow></TableHeader>
                    <TableBody>{recordingsData.recordings.map((rec) => (
                      <TableRow key={rec.id}>
                        <TableCell className="font-medium">{rec.device_name}</TableCell>
                        <TableCell>{new Date(rec.start_time).toLocaleString()}</TableCell>
                        <TableCell>{Math.floor(rec.duration_seconds / 60)}:{String(rec.duration_seconds % 60).padStart(2, '0')}</TableCell>
                        <TableCell>{(rec.size_bytes / 1024 / 1024).toFixed(1)} MB</TableCell>
                        <TableCell><Badge variant="outline">{rec.trigger}</Badge></TableCell>
                        <TableCell className="text-right"><Button variant="ghost" size="sm"><Play className="h-4 w-4" /></Button><Button variant="ghost" size="sm"><Download className="h-4 w-4" /></Button></TableCell>
                      </TableRow>
                    ))}</TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Album Tab */}
          <TabsContent value="album">
            <Card>
              <CardHeader><CardTitle>Local Album</CardTitle><CardDescription>Captured snapshots from your cameras</CardDescription></CardHeader>
              <CardContent>
                {snapshotsData.snapshots.length === 0 ? (
                  <div className="text-center py-12"><ImageLucide className="h-16 w-16 mx-auto text-muted-foreground mb-4" /><h3 className="text-lg font-medium">No snapshots yet</h3><p className="text-muted-foreground">Capture snapshots from live view</p></div>
                ) : (
                  <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
                    {snapshotsData.snapshots.map((snap) => (
                      <div key={snap.id} className="group relative aspect-video bg-gray-100 rounded-lg overflow-hidden cursor-pointer">
                        <img src={snap.url || '/placeholder.jpg'} alt={snap.device_name} className="w-full h-full object-cover" />
                        <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                          <Button size="sm" variant="secondary"><Eye className="h-4 w-4" /></Button>
                          <Button size="sm" variant="secondary"><Download className="h-4 w-4" /></Button>
                        </div>
                        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-2">
                          <p className="text-white text-xs truncate">{snap.device_name}</p>
                          <p className="text-white/70 text-xs">{new Date(snap.created_at).toLocaleDateString()}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Alerts Tab */}
          <TabsContent value="alerts">
            <Card>
              <CardHeader><CardTitle>Alerts & Notifications</CardTitle><CardDescription>System alerts and device notifications</CardDescription></CardHeader>
              <CardContent>
                {alertsData.alerts.length === 0 ? (
                  <div className="text-center py-12"><CheckCircle className="h-16 w-16 mx-auto text-green-500 mb-4" /><h3 className="text-lg font-medium">All Clear</h3><p className="text-muted-foreground">No active alerts</p></div>
                ) : (
                  <div className="space-y-3">{alertsData.alerts.map((alert) => {
                    const alertType = ALERT_TYPES[alert.alert_type] || ALERT_TYPES.offline;
                    const AlertIcon = alertType.icon;
                    return (
                      <div key={alert.id} className={`flex items-center gap-4 p-4 border rounded-lg ${alert.status === 'resolved' ? 'bg-muted' : ''}`}>
                        <AlertIcon className={`h-6 w-6 ${alertType.color}`} />
                        <div className="flex-1">
                          <div className="flex items-center gap-2"><p className="font-medium">{alert.message}</p><Badge variant={alert.severity === 'critical' ? 'destructive' : alert.severity === 'warning' ? 'secondary' : 'outline'}>{alert.severity}</Badge><Badge variant="outline">{alert.status}</Badge></div>
                          <p className="text-sm text-muted-foreground">{alert.device_name} &bull; {new Date(alert.created_at).toLocaleString()}</p>
                        </div>
                        {alert.status === 'active' && (
                          <div className="flex items-center gap-2">
                            <Button size="sm" variant="outline" onClick={() => acknowledgeAlertMutation.mutate(alert.id)}>Acknowledge</Button>
                            <Button size="sm" onClick={() => resolveAlertMutation.mutate(alert.id)}>Resolve</Button>
                          </div>
                        )}
                      </div>
                    );
                  })}</div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* AI Rules Tab */}
          <TabsContent value="ai-rules">
            <CCTVRulesEditor devices={devicesData.devices || []} />
          </TabsContent>

          {/* Settings Tab */}
          <TabsContent value="settings">
            <CCTVSettings settings={settings} onSave={(data) => updateSettingsMutation.mutate(data)} />
          </TabsContent>
        </Tabs>
      </div>

      {/* Add Device Dialog */}
      <Dialog open={showAddDevice} onOpenChange={setShowAddDevice}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Add New Device</DialogTitle><DialogDescription>Register a new camera or recording device</DialogDescription></DialogHeader>
          <CCTVDeviceForm onSubmit={(data) => addDeviceMutation.mutate(data)} onCancel={() => setShowAddDevice(false)} isLoading={addDeviceMutation.isPending} />
        </DialogContent>
      </Dialog>

      {/* Edit Device Dialog */}
      <Dialog open={!!editingDevice} onOpenChange={(open) => !open && setEditingDevice(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Edit Device</DialogTitle></DialogHeader>
          {editingDevice && <CCTVDeviceForm device={editingDevice} onSubmit={(data) => updateDeviceMutation.mutate({ id: editingDevice.id, data })} onCancel={() => setEditingDevice(null)} isLoading={updateDeviceMutation.isPending} />}
        </DialogContent>
      </Dialog>

      {/* Live View Dialog */}
      <CCTVLiveView device={selectedDevice} onClose={() => setSelectedDevice(null)} />
    </div>
  );
};

export default CCTVDashboard;
